import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow, addHours, addDays } from 'date-fns'
import { toast } from 'sonner'
import {
  Bell, BellOff, Check, Clock, MoreHorizontal, Trash2, AlarmClock
} from 'lucide-react'
import { reminderApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { Reminder, CRMEntityType } from '@/types'

interface ReminderListProps {
  entityType?: CRMEntityType
  entityId?: string
  showHeader?: boolean
  className?: string
  limit?: number
}

export function ReminderList({
  entityType,
  entityId,
  showHeader = true,
  className,
  limit = 10,
}: ReminderListProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['reminders', 'upcoming', entityType, entityId, limit],
    queryFn: () => reminderApi.getUpcomingReminders(limit),
  })

  const completeMutation = useMutation({
    mutationFn: (reminderId: string) => reminderApi.completeReminder(reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success(t('crm.reminderCompleted', 'Reminder completed'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const dismissMutation = useMutation({
    mutationFn: (reminderId: string) => reminderApi.dismissReminder(reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success(t('crm.reminderDismissed', 'Reminder dismissed'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const snoozeMutation = useMutation({
    mutationFn: ({ reminderId, snoozeUntil }: { reminderId: string; snoozeUntil: string }) =>
      reminderApi.snoozeReminder(reminderId, { snooze_until: snoozeUntil }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success(t('crm.reminderSnoozed', 'Reminder snoozed'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const deleteMutation = useMutation({
    mutationFn: (reminderId: string) => reminderApi.deleteReminder(reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      toast.success(t('messages.deleteSuccess'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.deleteError'))),
  })

  const handleSnooze = (reminderId: string, duration: 'hour' | 'day' | 'week') => {
    const now = new Date()
    let snoozeUntil: Date
    switch (duration) {
      case 'hour': snoozeUntil = addHours(now, 1); break
      case 'day': snoozeUntil = addDays(now, 1); break
      case 'week': snoozeUntil = addDays(now, 7); break
    }
    snoozeMutation.mutate({ reminderId, snoozeUntil: snoozeUntil.toISOString() })
  }

  if (isLoading) {
    return (
      <Card className={className}>
        {showHeader && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('crm.reminders', 'Reminders')}
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('messages.loadError', 'Failed to load data')}
        </CardContent>
      </Card>
    )
  }

  const reminders = data?.reminders || []

  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t('crm.reminders', 'Reminders')}
            {reminders.length > 0 && (
              <Badge variant="secondary" className="ml-2">{reminders.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        {reminders.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            {t('crm.noReminders', 'No upcoming reminders')}
          </div>
        ) : (
          <div className="space-y-2">
            {reminders.map((reminder) => (
              <ReminderItem
                key={reminder.reminder_id}
                reminder={reminder}
                onComplete={() => completeMutation.mutate(reminder.reminder_id)}
                onDismiss={() => dismissMutation.mutate(reminder.reminder_id)}
                onSnooze={(duration) => handleSnooze(reminder.reminder_id, duration)}
                onDelete={() => deleteMutation.mutate(reminder.reminder_id)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ReminderItemProps {
  reminder: Reminder
  onComplete: () => void
  onDismiss: () => void
  onSnooze: (duration: 'hour' | 'day' | 'week') => void
  onDelete: () => void
}

function ReminderItem({ reminder, onComplete, onDismiss, onSnooze, onDelete }: ReminderItemProps) {
  const { t } = useTranslation()
  const isPast = new Date(reminder.remind_at) < new Date()
  const isSnoozed = reminder.snoozed_until && new Date(reminder.snoozed_until) > new Date()

  return (
    <div className={cn(
      'flex items-start gap-3 p-3 rounded-lg border transition-colors',
      isPast && !isSnoozed && 'border-orange-200 bg-orange-50',
      isSnoozed && 'border-blue-200 bg-blue-50 opacity-75'
    )}>
      <div className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full',
        isPast && !isSnoozed ? 'bg-orange-100 text-orange-600' : 'bg-primary/10 text-primary'
      )}>
        {isSnoozed ? <AlarmClock className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{reminder.title}</p>

        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {isSnoozed ? (
            <span className="text-blue-600">
              Snoozed until {format(new Date(reminder.snoozed_until!), 'MMM d, h:mm a')}
            </span>
          ) : (
            <span className={isPast ? 'text-orange-600' : ''}>
              {formatDistanceToNow(new Date(reminder.remind_at), { addSuffix: true })}
            </span>
          )}
        </div>

        {reminder.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
            {reminder.description}
          </p>
        )}

        {reminder.entity_name && (
          <Badge variant="outline" className="mt-2 text-xs">
            {reminder.entity_type}: {reminder.entity_name}
          </Badge>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onComplete}>
            <Check className="h-4 w-4 mr-2" />
            {t('crm.complete', 'Complete')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDismiss}>
            <BellOff className="h-4 w-4 mr-2" />
            {t('crm.dismiss', 'Dismiss')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onSnooze('hour')}>
            <AlarmClock className="h-4 w-4 mr-2" />
            {t('crm.snooze1Hour', 'Snooze 1 hour')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSnooze('day')}>
            <AlarmClock className="h-4 w-4 mr-2" />
            {t('crm.snooze1Day', 'Snooze 1 day')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSnooze('week')}>
            <AlarmClock className="h-4 w-4 mr-2" />
            {t('crm.snooze1Week', 'Snooze 1 week')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" />
            {t('common.delete', 'Delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default ReminderList

